import { Alert, Button, Group, Modal, Stack, Text } from "@mantine/core";
import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import type { RunnerDisplayResponse } from "./api";
import { isRunnerNotFound, runnerErrorText } from "./errors";
import { useRetireRunner } from "./useRetireRunner";

interface RetireRunnerModalProps {
  readonly runner: RunnerDisplayResponse | null;
  readonly onClose: () => void;
}

// Подтверждение снятия раннера с учёта: необратимое действие, поэтому
// отдельное предупреждение, что отменённые задачи раннера после снятия
// не перезапустить. Отказ `409` (в очереди есть блокирующие задачи)
// показывается с числом задач, модалка остаётся открытой; после `404`
// подтверждение недоступно (раннера уже нет); закрытие
// (отмена, крестик, успех) сбрасывает локальную ошибку и мутацию; пока
// DELETE в полёте, закрыть диалог нельзя.
export function RetireRunnerModal({
  runner,
  onClose,
}: RetireRunnerModalProps): ReactElement {
  const { t } = useTranslation();
  const retire = useRetireRunner();
  const [error, setError] = useState<string | null>(null);

  const pending = retire.isPending;
  // После `404` раннера уже нет — повторное снятие дало бы тот же ответ.
  const runnerGone = isRunnerNotFound(retire.error);

  const close = (): void => {
    setError(null);
    retire.reset();
    onClose();
  };

  return (
    <Modal
      opened={runner !== null}
      onClose={close}
      title={t("runners.retire.confirmTitle")}
      centered
      closeOnClickOutside={!pending}
      closeOnEscape={!pending}
      withCloseButton={!pending}
    >
      {runner !== null ? (
        <Stack gap="md">
          <Text>
            {t("runners.retire.confirmText", {
              name: runner.name ?? runner.runner_id,
            })}
          </Text>
          <Alert color="yellow">{t("runners.retire.cancelledWarning")}</Alert>
          {error !== null ? (
            <Alert color="red" title={t("runners.errorHeading")}>
              {error}
            </Alert>
          ) : null}
          <Group justify="flex-end">
            <Button variant="default" onClick={close} disabled={pending}>
              {t("runners.retire.cancelButton")}
            </Button>
            <Button
              color="red"
              onClick={() => {
                setError(null);
                retire.mutate(runner.runner_id, {
                  onSuccess: close,
                  onError: (err) => {
                    setError(runnerErrorText(err, t));
                  },
                });
              }}
              loading={pending}
              disabled={runnerGone}
            >
              {t("runners.retire.confirmButton")}
            </Button>
          </Group>
        </Stack>
      ) : null}
    </Modal>
  );
}
